import { Autocomplete } from '@yoast/ui-library';
import { useMemo, useState } from 'react';

import {
  canonicalizeCountry,
  commonCountryOptionsFlat,
} from '../../utils/formOptions';

// The onboarding profile stores an ISO 3166-1 alpha-2 code (or the "Global"
// worldwide sentinel), so "Other" (which has no code) is dropped — an unlisted
// country is left empty instead.
const COUNTRY_OPTIONS = commonCountryOptionsFlat.filter(
  (option) => option.value !== 'OTHER',
);

type Props = {
  /** The currently-selected ISO alpha-2 code, or null. */
  value: string | null;
  /** Called with the selected ISO code, or null when cleared. */
  onChange: (code: string | null) => void;
};

/**
 * Searchable country picker for the brand-profile sidebar. The user searches and
 * selects by country name; the value handed back is always the ISO alpha-2 code.
 */
const CountrySelect = ({ value, onChange }: Props) => {
  const [query, setQuery] = useState('');

  // Normalize incoming codes (trim + uppercase) so a lowercase/whitespace-padded
  // value (e.g. from previously stored data) still matches the option list, which
  // is uppercase. The "Global" sentinel keeps its mixed case. Consistent with
  // countryLabel's case-insensitive lookup.
  const normalizedValue = canonicalizeCountry(value);

  const selectedLabel = useMemo(() => {
    if (!normalizedValue) return '';
    return (
      COUNTRY_OPTIONS.find((option) => option.value === normalizedValue)?.label
      ?? normalizedValue
    );
  }, [normalizedValue]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (option) =>
        option.label.toLowerCase().includes(normalized)
        || option.value.toLowerCase().includes(normalized),
    );
  }, [query]);

  return (
    <Autocomplete
      id="onboarding-country-select"
      label="Country"
      labelProps={{ className: 'yst-sr-only' }}
      value={normalizedValue}
      nullable
      placeholder="Search countries…"
      onQueryChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setQuery(event.target.value)
      }
      onChange={(next: string | null) => {
        onChange(canonicalizeCountry(next));
        setQuery('');
      }}
      selectedLabel={selectedLabel}
    >
      {filtered.map((country) => (
        <Autocomplete.Option key={country.value} value={country.value}>
          {country.label}
        </Autocomplete.Option>
      ))}
    </Autocomplete>
  );
};

export default CountrySelect;
